import type { ReactNode } from "react";

import type { UseCmmdProjectLinkResult } from "../hooks/useCmmdProjectLink";
import { SidebarInset } from "./ui/sidebar";
import {
  NoActiveThreadStateDialogs,
  type MissingForgeProjectPromptState,
  type StartThreadPromptState,
} from "./NoActiveThreadStateDialogs";

export interface NoActiveThreadStateContentProps {
  readonly cmmdProjectLink: UseCmmdProjectLinkResult;
  readonly shouldShowCompactErrorBanner: boolean;
  readonly compactError: string | null;
  readonly commandCenterTaskSurface: ReactNode;
  readonly startThreadPrompt: StartThreadPromptState | null;
  readonly missingForgeProjectPrompt: MissingForgeProjectPromptState | null;
  readonly missingForgeProjectImporting: boolean;
  readonly projectLinkSheetOpen: boolean;
  readonly projectLinkSheetMode: "link" | "create";
  readonly onStartThreadPromptOpenChange: (open: boolean) => void;
  readonly onConfirmStartThreadPrompt: () => void;
  readonly onMissingForgeProjectPromptChange: (
    prompt: MissingForgeProjectPromptState | null,
  ) => void;
  readonly onProjectLinkSheetModeChange: (mode: "link" | "create") => void;
  readonly onProjectLinkSheetOpenChange: (open: boolean) => void;
  readonly onImportMissingForgeProject: () => void;
}

export function NoActiveThreadStateContent(props: NoActiveThreadStateContentProps) {
  return (
    <SidebarInset className="h-full min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden bg-background">
        {/* No empty bar.
            This held an empty `<header>` kept as a stand-in after 06e7fda26
            deleted the project workspace that used to fill it, on the grounds
            that the height was Electron's titlebar drag region. It was not:
            dragging needs the `.drag-region` class (see `index.css`) and this
            element never carried it. What it did carry was a `border-b`, so the
            surface opened with a ruled empty strip under the global bar — the
            same dead band `/usage` and settings each removed. */}
        {props.shouldShowCompactErrorBanner && props.compactError ? (
          <div className="mx-4 mt-2 rounded-lg border border-destructive-border bg-destructive-surface px-3 py-2.5 sm:mx-5">
            <p className="text-sm text-destructive/90">{props.compactError}</p>
          </div>
        ) : null}

        {props.commandCenterTaskSurface}
      </div>

      <NoActiveThreadStateDialogs
        startThreadPrompt={props.startThreadPrompt}
        missingForgeProjectPrompt={props.missingForgeProjectPrompt}
        missingForgeProjectImporting={props.missingForgeProjectImporting}
        projectLinkSheetOpen={props.projectLinkSheetOpen}
        projectLinkSheetMode={props.projectLinkSheetMode}
        cmmdProjectLink={props.cmmdProjectLink}
        onStartThreadPromptOpenChange={props.onStartThreadPromptOpenChange}
        onConfirmStartThreadPrompt={props.onConfirmStartThreadPrompt}
        onMissingForgeProjectPromptChange={props.onMissingForgeProjectPromptChange}
        onProjectLinkSheetModeChange={props.onProjectLinkSheetModeChange}
        onProjectLinkSheetOpenChange={props.onProjectLinkSheetOpenChange}
        onImportMissingForgeProject={props.onImportMissingForgeProject}
      />
    </SidebarInset>
  );
}
